import React, { useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import { todayKST } from '../../../utils/date';

const EMPTY_RULE = {
  effective_from: '',
  effective_to: '',
  salary_enabled: true,
  monthly_salary: '',
  incentive_source: 'direct',
  incentive_unit_price: '250',
  incentive_base_included_count: '0',
  leave_deduction_enabled: true,
  free_leave_days: '1',
  reorder_excluded: true,
  cancel_deduction_enabled: true,
  notes: '',
};

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12 },
  hero: { border: '1px solid rgba(37,99,235,0.24)', borderRadius: 12, background: 'rgba(37,99,235,0.07)', padding: 12 },
  heroTitle: { fontSize: 13, fontWeight: 950, color: 'var(--text)', marginBottom: 4 },
  helper: { fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.45 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(190px,100%),1fr))', gap: 10, minWidth: 0 },
  section: { border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--bg-subtle)', minWidth: 0, boxSizing: 'border-box' },
  sectionTop: { display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 9 },
  sectionTitle: { fontSize: 13, fontWeight: 950, color: 'var(--text)' },
  field: { display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 },
  label: { fontSize: 11, fontWeight: 900, color: 'var(--text-muted)' },
  input: { height: 40, width: '100%', minWidth: 0, maxWidth: '100%', border: '1px solid var(--border)', borderRadius: 9, padding: '0 10px', background: 'var(--bg-card)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  toggle: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-sub)', fontWeight: 900 },
  modeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(150px,100%),1fr))', gap: 8 },
  mode: (active) => ({
    minHeight: 74,
    border: active ? '2px solid #2563eb' : '1px solid var(--border)',
    borderRadius: 11,
    padding: 10,
    textAlign: 'left',
    background: active ? 'rgba(37,99,235,0.09)' : 'var(--bg-card)',
    color: 'var(--text)',
    cursor: 'pointer',
  }),
  modeTitle: { fontSize: 12, fontWeight: 950, marginBottom: 4 },
  modeText: { fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.35 },
  err: { fontSize: 12, color: '#dc2626', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: 8 },
  footer: { display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  footerRight: { display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' },
  btn: { minHeight: 40, border: '1px solid var(--border)', borderRadius: 8, padding: '0 12px', background: 'var(--bg-card)', color: 'var(--text-sub)', fontSize: 12, fontWeight: 900, cursor: 'pointer' },
  primary: { minHeight: 40, border: 'none', borderRadius: 8, padding: '0 15px', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 950, cursor: 'pointer' },
  danger: { border: '1px solid rgba(220,38,38,0.25)', color: '#dc2626', background: 'rgba(220,38,38,0.07)' },
};

function numericText(value) {
  const raw = String(value ?? '').replace(/,/g, '');
  return raw === '' || /^\d+(\.\d{0,2})?$/.test(raw) ? raw : null;
}

function moneyInput(value) {
  const raw = String(value ?? '').replace(/,/g, '');
  if (raw === '') return '';
  if (!/^\d+$/.test(raw)) return null;
  return Number(raw).toLocaleString('ko-KR');
}

function incentiveSource(rule) {
  return rule?.incentive_source || (rule?.incentive_enabled === false ? 'none' : 'direct');
}

function ruleToForm(rule, fallbackDate, mode) {
  if (!rule) return { ...EMPTY_RULE, effective_from: fallbackDate || todayKST() };
  return {
    effective_from: mode === 'schedule' ? (fallbackDate || todayKST()) : (rule.effective_from || fallbackDate || todayKST()),
    effective_to: mode === 'schedule' ? '' : (rule.effective_to || ''),
    salary_enabled: !!rule.salary_enabled,
    monthly_salary: Number(rule.monthly_salary || 0).toLocaleString('ko-KR'),
    incentive_source: incentiveSource(rule),
    incentive_unit_price: Number(rule.incentive_unit_price || 0).toLocaleString('ko-KR'),
    incentive_base_included_count: Number(rule.incentive_base_included_count || 0).toLocaleString('ko-KR'),
    leave_deduction_enabled: !!rule.leave_deduction_enabled,
    free_leave_days: String(Number(rule.free_leave_days || 0)),
    reorder_excluded: rule.reorder_excluded !== false,
    cancel_deduction_enabled: rule.cancel_deduction_enabled !== false,
    notes: rule.notes || '',
  };
}

export function toBillingRulePayload(form) {
  const source = form.incentive_source || 'direct';
  return {
    effective_from: form.effective_from,
    effective_to: form.effective_to || null,
    salary_enabled: !!form.salary_enabled,
    monthly_salary: Number(String(form.monthly_salary || '0').replace(/,/g, '')) || 0,
    incentive_enabled: source !== 'none',
    incentive_source: source,
    incentive_unit_price: Number(String(form.incentive_unit_price || '0').replace(/,/g, '')) || 0,
    incentive_base_included_count: Number(String(form.incentive_base_included_count || '0').replace(/,/g, '')) || 0,
    leave_deduction_enabled: !!form.leave_deduction_enabled,
    free_leave_days: Number(form.free_leave_days || 0),
    reorder_excluded: !!form.reorder_excluded,
    cancel_deduction_enabled: !!form.cancel_deduction_enabled,
    notes: form.notes || null,
  };
}

export function BillingRuleFormModal({
  mode = 'edit',
  assignment,
  rule,
  templateRule,
  defaultEffectiveFrom,
  lockedThrough,
  saving,
  error,
  onSave,
  onDelete,
  onClose,
}) {
  const title = mode === 'schedule' ? '급여 조건 바꾸기 예약' : '급여 조건 고치기';
  const sourceRule = rule || templateRule;
  const [form, setForm] = useState(() => ruleToForm(sourceRule, defaultEffectiveFrom || assignment?.start_date?.slice(0, 10), mode));
  const scheduleText = useMemo(() => {
    if (mode !== 'schedule') return '';
    const from = form.effective_from || defaultEffectiveFrom || todayKST();
    return lockedThrough
      ? `${lockedThrough}까지의 확정 정산은 그대로 두고, ${from}부터 새 조건을 적용합니다.`
      : `${from}부터 새 조건을 적용합니다.`;
  }, [defaultEffectiveFrom, form.effective_from, lockedThrough, mode]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const footer = (
    <div style={S.footer}>
      <div>
        {mode === 'edit' && rule && (
          <button type="button" style={{ ...S.btn, ...S.danger }} onClick={() => onDelete(rule)} disabled={saving}>삭제</button>
        )}
      </div>
      <div style={S.footerRight}>
        <button type="button" style={S.btn} onClick={onClose} disabled={saving}>취소</button>
        <button type="button" style={S.primary} onClick={() => onSave(toBillingRulePayload(form))} disabled={saving}>
          {saving ? '저장 중' : mode === 'schedule' ? '예약 저장' : '저장'}
        </button>
      </div>
    </div>
  );

  return (
    <Modal open title={title} icon="💰" onClose={onClose} footer={footer} width="min(760px, calc(100vw - 48px))" mobileSheet mobileSheetDragClose>
      <div style={S.wrap}>
        <div style={S.hero}>
          <div style={S.heroTitle}>{assignment?.marketer_name} → {assignment?.consultant_name}</div>
          <div style={S.helper}>
            {mode === 'schedule'
              ? scheduleText
              : '확정된 월에 사용되지 않은 미래 조건만 직접 수정합니다.'}
          </div>
        </div>

        <div style={S.grid}>
          <label style={S.field}>
            <span style={S.label}>적용 시작일</span>
            <input type="date" style={S.input} value={form.effective_from} onChange={(e) => setField('effective_from', e.target.value)} />
          </label>
          <label style={S.field}>
            <span style={S.label}>적용 끝나는 날</span>
            <input type="date" style={S.input} value={form.effective_to} onChange={(e) => setField('effective_to', e.target.value)} />
          </label>
        </div>

        <div style={S.grid}>
          <div style={S.section}>
            <div style={S.sectionTop}>
              <div style={S.sectionTitle}>기본급</div>
              <label style={S.toggle}><input type="checkbox" checked={form.salary_enabled} onChange={(e) => setField('salary_enabled', e.target.checked)} /> 반영</label>
            </div>
            <label style={S.field}>
              <span style={S.label}>월급</span>
              <input
                type="text"
                inputMode="numeric"
                style={S.input}
                value={form.monthly_salary}
                disabled={!form.salary_enabled}
                onChange={(e) => {
                  const next = moneyInput(e.target.value);
                  if (next !== null) setField('monthly_salary', next);
                }}
              />
            </label>
          </div>

          <div style={S.section}>
            <div style={S.sectionTop}>
              <div style={S.sectionTitle}>연차 차감</div>
              <label style={S.toggle}><input type="checkbox" checked={form.leave_deduction_enabled} onChange={(e) => setField('leave_deduction_enabled', e.target.checked)} /> 반영</label>
            </div>
            <label style={S.field}>
              <span style={S.label}>무료 연차 일수</span>
              <input
                type="text"
                inputMode="decimal"
                style={S.input}
                value={form.free_leave_days}
                disabled={!form.leave_deduction_enabled}
                onChange={(e) => {
                  const next = numericText(e.target.value);
                  if (next !== null) setField('free_leave_days', next);
                }}
              />
            </label>
          </div>
        </div>

        <div style={S.section}>
          <div style={S.sectionTitle}>인센티브 반영 방식</div>
          <div style={{ ...S.modeGrid, marginTop: 8 }}>
            <button type="button" style={S.mode(form.incentive_source === 'direct')} onClick={() => setField('incentive_source', 'direct')}>
              <div style={S.modeTitle}>장수로 직접 계산</div>
              <div style={S.modeText}>기본 포함 장수를 넘은 장수만큼 주고, 취소가 더 많으면 빼요.</div>
            </button>
            <button type="button" style={S.mode(form.incentive_source === 'incentive_total')} onClick={() => setField('incentive_source', 'incentive_total')}>
              <div style={S.modeTitle}>인센티브 관리 금액</div>
              <div style={S.modeText}>인센티브 관리의 이 직원 월 금액을 가져와요.</div>
            </button>
            <button type="button" style={S.mode(form.incentive_source === 'none')} onClick={() => setField('incentive_source', 'none')}>
              <div style={S.modeTitle}>계산 안 함</div>
              <div style={S.modeText}>1:1급여에 인센티브를 넣지 않아요.</div>
            </button>
          </div>

          {form.incentive_source === 'direct' && (
            <>
              <div style={{ ...S.grid, marginTop: 10 }}>
                <label style={S.field}>
                  <span style={S.label}>기본 포함 장수</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={S.input}
                    value={form.incentive_base_included_count}
                    onChange={(e) => {
                      const next = numericText(e.target.value.replace(/,/g, ''));
                      if (next !== null) setField('incentive_base_included_count', next === '' ? '' : Number(next).toLocaleString('ko-KR'));
                    }}
                  />
                </label>
                <label style={S.field}>
                  <span style={S.label}>1장당 인센티브</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    style={S.input}
                    value={form.incentive_unit_price}
                    onChange={(e) => {
                      const next = moneyInput(e.target.value);
                      if (next !== null) setField('incentive_unit_price', next);
                    }}
                  />
                </label>
              </div>
              <div style={{ ...S.grid, marginTop: 10 }}>
                <label style={S.toggle}><input type="checkbox" checked={form.reorder_excluded} onChange={(e) => setField('reorder_excluded', e.target.checked)} /> 재계약 제외</label>
                <label style={S.toggle}><input type="checkbox" checked={form.cancel_deduction_enabled} onChange={(e) => setField('cancel_deduction_enabled', e.target.checked)} /> 취소 장수 차감</label>
              </div>
            </>
          )}
        </div>

        <label style={S.field}>
          <span style={S.label}>메모</span>
          <input type="text" style={S.input} value={form.notes} onChange={(e) => setField('notes', e.target.value)} />
        </label>

        {error && <div style={S.err}>{error}</div>}
      </div>
    </Modal>
  );
}
